import { Controller } from '@hotwired/stimulus';
import { gsap } from 'gsap';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Gap between elements that are already on-screen at page load, so the hero
// (title → subtitle → CTA → arrow → steps) cascades in DOM order rather than
// all at once. Kept short to stay snappy.
const INITIAL_STAGGER = 0.09;

// Fade + slide each `animate` target into view on scroll.
export default class extends Controller {
  static targets = ['animate'];

  connect() {
    this.__setupAnimations();
  }

  disconnect() {
    if (this.__observer) this.__observer.disconnect();
  }

  // private

  get __reducedMotion() {
    return window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  __setupAnimations() {
    if (this.__reducedMotion) {
      this.animateTargets.forEach((el) => gsap.set(el, { opacity: 1, y: 0, filter: 'none', clearProps: 'transform' }));
      return;
    }

    this.animateTargets.forEach((el) => {
      gsap.set(el, { opacity: 0, y: 28, filter: 'blur(10px)' });
    });

    // First observer callback delivers everything already in view at load as a
    // single batch — cascade those in DOM order. Later (scroll) reveals fire
    // one at a time and use each element's own `data-delay`.
    this.__initialReveal = true;
    this.__observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (this.__initialReveal) {
          this.__initialReveal = false;
          // Walk a running delay so elements can opt into an extra lead-in via
          // `data-cascade-gap` (e.g. the step panels pause after the arrow).
          let delay = 0;
          visible
            .sort((a, b) => this.animateTargets.indexOf(a.target) - this.animateTargets.indexOf(b.target))
            .forEach((entry) => {
              delay += parseFloat(entry.target.dataset.cascadeGap || 0);
              this.__animateIn(entry.target, delay);
              this.__observer.unobserve(entry.target);
              delay += INITIAL_STAGGER;
            });
          return;
        }
        visible.forEach((entry) => {
          this.__animateIn(entry.target);
          this.__observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' },
    );
    this.animateTargets.forEach((el) => this.__observer.observe(el));
  }

  __animateIn(el, delayOverride) {
    const delay = delayOverride ?? parseFloat(el.dataset.delay || 0);

    gsap.to(el, {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 0.9,
      delay,
      ease: 'power3.out',
      // Clear inline transform after the intro so CSS :hover rules can
      // override it (otherwise GSAP's translate3d(0,0,0) sticks around and
      // beats any class-level hover transform via inline-style specificity).
      // Filter too: a leftover blur(0px) keeps the element on its own compositing layer.
      clearProps: 'transform,filter',
    });
  }
}
